import React from 'react';
import { Car, Edit, Trash2, Fuel, Calendar, Gauge, Loader2, ClipboardPaste } from 'lucide-react';
import { Button } from '../ui/button';
import VehicleModelImageUpload from '../VehicleModelImageUpload';

const FUEL_LABELS = { essence: 'Essence', diesel: 'Diesel', hybride: 'Hybride', 'essence/électrique': 'Essence/électrique', 'diesel/électrique': 'Diesel/électrique', 'essence/gpl': 'Essence / gaz combustible liquéfié (GPL)', 'essence/éthanol': 'Essence/éthanol', 'essence/éthanol/gaz': 'Essence / éthanol / gaz', 'électrique': 'Électrique', gpl: 'GPL' };
const FUEL_COLORS = {
  essence: 'bg-yellow-100 text-yellow-800',
  diesel: 'bg-gray-700 text-white',
  hybride: 'bg-green-100 text-green-800',
  'essence/électrique': 'bg-emerald-100 text-emerald-800',
  'diesel/électrique': 'bg-teal-100 text-teal-800',
  'essence/gpl': 'bg-fuchsia-100 text-fuchsia-800',
  'essence/éthanol': 'bg-lime-100 text-lime-800',
  'essence/éthanol/gaz': 'bg-orange-100 text-orange-800',
  'électrique': 'bg-blue-100 text-blue-800',
  gpl: 'bg-purple-100 text-purple-800',
};

const AdminVehicleDetails = ({ selectedVehicle, vehicleDetails, onPasteEngines, onEdit, onDelete, onImageChanged }) => (
  <div className="lg:col-span-2 bg-white rounded-lg shadow-sm overflow-hidden">
    {!selectedVehicle ? (
      <div className="h-full flex items-center justify-center p-12">
        <div className="text-center">
          <Car size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Sélectionnez un véhicule pour voir ses détails</p>
        </div>
      </div>
    ) : vehicleDetails ? (
      <div>
        <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-orange-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{vehicleDetails.brand} {vehicleDetails.model}</h2>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {vehicleDetails.years?.length > 0 && `${Math.min(...vehicleDetails.years)} - ${Math.max(...vehicleDetails.years)}`}
                </span>
                <span className="flex items-center gap-1">
                  <Gauge size={14} />
                  {Object.values(vehicleDetails.engines || {}).flat().length} motorisations
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="admin-vehicle-paste-engines-button" onClick={onPasteEngines}>
                <ClipboardPaste size={14} className="mr-1" /> Coller les motorisations
              </Button>
              <Button variant="outline" size="sm" className="text-blue-600" data-testid="admin-vehicle-edit-button" onClick={onEdit}>
                <Edit size={14} className="mr-1" /> Modifier
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(vehicleDetails.brand, vehicleDetails.model)}>
                <Trash2 size={14} className="mr-1" /> Supprimer
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4 border-b">
          <VehicleModelImageUpload brand={vehicleDetails.brand} model={vehicleDetails.model} image={vehicleDetails.image} onChanged={onImageChanged} />
        </div>
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Calendar size={16} /> Années disponibles</h3>
          <div className="flex flex-wrap gap-2">
            {vehicleDetails.years?.sort((a, b) => b - a).map(year => (
              <span key={year} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">{year}</span>
            ))}
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Fuel size={16} /> Motorisations par période</h3>
          <div className="space-y-4">
            {Object.keys(vehicleDetails.engines || {}).length === 0 && (
              <button type="button" onClick={onPasteEngines} className="w-full rounded-lg border-2 border-dashed border-orange-200 bg-orange-50/50 p-4 text-sm text-gray-600 hover:border-orange-400 hover:bg-orange-50 transition-colors" data-testid="admin-vehicle-no-engines">
                Aucune motorisation pour ce modèle. <span className="font-medium text-orange-600">Cliquez ici pour coller la liste des moteurs.</span>
              </button>
            )}
            {Object.entries(vehicleDetails.engines || {}).map(([period, engines]) => (
              <div key={period} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 font-medium text-sm text-gray-600">Période: {period}</div>
                <div className="divide-y">
                  {engines.map((engine, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">{engine.name}</span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {engine.displacement > 0 && `${engine.displacement} cm³ • `}
                          {engine.power}ch ({engine.power_kw}kW) • {engine.engine_code}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${FUEL_COLORS[engine.fuel] || 'bg-gray-100 text-gray-800'}`}>{FUEL_LABELS[engine.fuel] || engine.fuel}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="h-full flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    )}
  </div>
);

export default AdminVehicleDetails;
